import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeSVG } from 'qrcode.react';
import type { IdentificadorLoja } from '../../api';
import { montarUrlIdentificador } from '../../lib/url-identificador';
import { ROTULO_TIPO_IDENTIFICADOR } from './icone-modo';

/**
 * QR code de um ponto de acesso — o mesmo diálogo na tela de acessos e no card
 * de "Pontos de acesso" das abas de configuração.
 *
 * O fundo branco atrás do código é obrigatório, não decoração: no tema escuro o
 * SVG herdaria o fundo da página e nenhum leitor consegue ler um QR de baixo
 * contraste.
 */
export function DialogQrCode({
  identificador,
  aoFechar,
}: {
  /** `null` = fechado. */
  identificador: IdentificadorLoja | null;
  aoFechar: () => void;
}): JSX.Element {
  const [copiado, setCopiado] = useState(false);
  const url = identificador ? montarUrlIdentificador(identificador) : '';

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
    } catch {
      /* clipboard indisponível — o endereço está à vista para copiar à mão */
    }
  }

  return (
    <>
      <Dialog open={!!identificador} onClose={aoFechar} fullWidth maxWidth="sm">
        <DialogTitle>
          QR code · {identificador ? ROTULO_TIPO_IDENTIFICADOR[identificador.tipo] : ''}{' '}
          {identificador?.numero?.trim() ?? ''}
        </DialogTitle>

        <DialogContent>
          {identificador && (
            <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
              <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 1 }}>
                <QRCodeSVG value={url} size={256} level="M" />
              </Box>

              <Typography variant="body2" color="text.secondary" align="center" sx={{ wordBreak: 'break-all' }}>
                {url}
              </Typography>

              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => void copiar()}>
                Copiar link
              </Button>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={aoFechar}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copiado}
        autoHideDuration={2000}
        onClose={() => setCopiado(false)}
        message="Link copiado"
      />
    </>
  );
}
